import { addDays, format, parseISO, startOfMonth, endOfMonth, add, subMonths, addMonths } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'

import dayGridPlugin from '@fullcalendar/daygrid'
import timeGridPlugin from '@fullcalendar/timegrid'
import interactionPlugin from '@fullcalendar/interaction'
import FullCalendar from '@fullcalendar/react'
import ptBrLocale from '@fullcalendar/core/locales/pt-br'
import * as Yup from 'yup'
import './style.css'

import { 
  background, 
  Box, 
  Flex, 
  Heading, 
  Spinner, 
  Text, 
  useDisclosure, 
  useToast,
  Button,
  ButtonGroup,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  HStack,
  VStack
} from '@chakra-ui/react'
import { Header } from '../Header'
import { Sidebar } from '../Sidebar'

import api from '../../Services/api'
import { AlertDialogCustom } from '../AlertDialog'
import { useNavigate } from 'react-router-dom'
import ISchedulesProps from '../../interfaces/ISchedulesProps'
import { ModalAppointmentDetails } from '../ModalAppointmentDetails'
import { IPropertiesProps } from '../../interfaces/IPropertiesProps'
import { Pagination } from '../Pagination'

interface ClassEvent {
  title: string
  date: string
}

interface DaySelected {
  day: string
  formatted: string
}

const EventScheme = Yup.object().shape({
  name: Yup.string().required('Nome do evento é obrigatório'),
  teacher: Yup.string().required('Nome do professor é obrigatório'),
  hour: Yup.string().required('Horário de liberação é obrigatório'),
  link: Yup.string().required('Link do evento é obrigatório')
})

interface Props {
  schedule: ISchedulesProps[]
  reloadFunction: (page?: number, properties?: string[], partners?: string[], upcomingSchedules?: boolean, startDate?: string, endDate?: string, byCreatedAt?: boolean, byCheckIn?: boolean, showNormal?: boolean, showSpecial?: boolean) => void
  // Current filters to maintain when switching modes
  currentFilters: {
    properties?: string[]
    partners?: string[]
    upcomingSchedules?: boolean
    byCreatedAt?: boolean
    byCheckIn?: boolean
    showNormal?: boolean
    showSpecial?: boolean
    startDate?: string
  }
}

interface ItemSelectedProps {
  show: boolean
  id: string
}

// Adicionar enum para modo de visualização
enum ViewMode {
  AGENDA = 'agenda',
  LIST = 'list'
}

const CalendarSchedule: FC<Props> = ({ schedule, reloadFunction, currentFilters }) => {
  const [selectedItem, setSelectedItem] = useState<ItemSelectedProps>({ show: false, id: '' })
  const [viewMode, setViewMode] = useState<ViewMode>(ViewMode.AGENDA)
  const [currentPage, setCurrentPage] = useState(1)
  const [currentCalendarDate, setCurrentCalendarDate] = useState(new Date())
  const calendarRef = useRef<FullCalendar>(null)
  
  const itemsPerPage = 10

  // Função para calcular as datas de 3 meses (anterior, atual, próximo)
  const getThreeMonthsRange = useCallback((referenceDate: Date) => {
    const previousMonth = subMonths(referenceDate, 1)
    const nextMonth = addMonths(referenceDate, 1)
    
    const startDate = format(startOfMonth(previousMonth), 'yyyy-MM-dd')
    const endDate = format(endOfMonth(nextMonth), 'yyyy-MM-dd')
    
    return { startDate, endDate }
  }, [])

  const appointmentsData = useMemo(() => {
    return schedule.map(item => {
      console.log('Original checkIn:', item.checkIn)
      console.log('Original checkOut:', item.checkOut)
      console.log('Original date_initial:', item.date_initial)
      console.log('Original date_final:', item.date_final)
      
      // Tentar usar checkIn/checkOut primeiro, depois date_initial/date_final como fallback
      const startDateStr = item.checkIn || item.date_initial
      const endDateStr = item.checkOut || item.date_final
      
      const adjustedStartDate = parseISO(String(startDateStr))
      // Para eventos allDay, a data de fim deve ser exclusiva (dia seguinte)
      const adjustedEndDate = addDays(parseISO(String(endDateStr)), 1)
      
      console.log('Parsed startDate:', adjustedStartDate)
      console.log('Parsed endDate:', adjustedEndDate)
      console.log('---')

      // Determinar o tipo de evento baseado na duração
      const startDate = format(adjustedStartDate, 'yyyy-MM-dd')
      const endDate = format(adjustedEndDate, 'yyyy-MM-dd')
      const isMultiDay = startDate !== endDate
      
      // Aplicar classes específicas baseadas no tipo de evento
      let eventClasses = ['checkin-event']
      
      if (isMultiDay) {
        // Evento de múltiplos dias
        eventClasses.push('multi-day-event')
      } else {
        // Evento de um único dia
        eventClasses.push('single-day-event')
      }

      return {
        title: `${item.property.name} ${item.isSpecialReservation ? '(Especial)' : ''} - ${item.user.name}`,
        start: adjustedStartDate,
        end: adjustedEndDate,
        id: item.id,
        backgroundColor: item.property.color,
        borderWidth: 0,
        allDay: true, // Define como evento de dia inteiro para aparecer na área compacta
        classNames: eventClasses // Classes específicas baseadas no tipo de evento
      }
    })
  }, [schedule])

  // Paginação para modo lista (usando dados já filtrados pelo backend)
  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage
    const endIndex = startIndex + itemsPerPage
    return schedule.slice(startIndex, endIndex)
  }, [schedule, currentPage, itemsPerPage])

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
  }

  const handleUpdateEvent = useCallback((e: any) => {
    // setValue('name', 'value', { shouldValidate: true })
    console.log('update: ', e.event)
    setSelectedItem({
      id: e.event._def.publicId,
      show: true
    })
  }, [])

  // Function to handle calendar navigation (prev/next month)
  const handleCalendarNavigation = useCallback((info: any) => {
    const newDate = new Date(info.view.currentStart)
    setCurrentCalendarDate(newDate)
    
    // Only reload data if we're in agenda mode
    if (viewMode === ViewMode.AGENDA) {
      const { startDate, endDate } = getThreeMonthsRange(newDate)
      reloadFunction(
        1,
        currentFilters.properties,
        currentFilters.partners,
        currentFilters.upcomingSchedules,
        startDate,
        endDate,
        currentFilters.byCreatedAt,
        currentFilters.byCheckIn,
        currentFilters.showNormal,
        currentFilters.showSpecial
      )
    }
  }, [viewMode, getThreeMonthsRange, reloadFunction, currentFilters])

  // Quando o usuário aplica filtros com um novo startDate, navega o calendário para esse mês
  useEffect(() => {
    if (!currentFilters.startDate) return
    const target = parseISO(currentFilters.startDate)
    if (Number.isNaN(target.getTime())) return
    if (format(target, 'yyyy-MM') === format(currentCalendarDate, 'yyyy-MM')) return
    setCurrentCalendarDate(target)
    calendarRef.current?.getApi().gotoDate(target)
  }, [currentFilters.startDate])

  // Effect to load 3 months data when component mounts in agenda mode
  useEffect(() => {
    if (viewMode === ViewMode.AGENDA) {
      const { startDate, endDate } = getThreeMonthsRange(currentCalendarDate)
      reloadFunction(
        1,
        currentFilters.properties,
        currentFilters.partners,
        currentFilters.upcomingSchedules,
        startDate,
        endDate,
        currentFilters.byCreatedAt,
        currentFilters.byCheckIn,
        currentFilters.showNormal,
        currentFilters.showSpecial
      )
    }
  }, []) // Empty dependency array to run only on mount

  const handleViewItemDetails = (itemId: string) => {
    setSelectedItem({
      id: itemId,
      show: true
    })
  }

  // Function to handle view mode change
  const handleViewModeChange = (newMode: ViewMode) => {
    setViewMode(newMode)
    
    // If switching to list mode, call reloadFunction with current month dates and current filters
    if (newMode === ViewMode.LIST) {
      const now = new Date()
      const startDate = format(startOfMonth(now), 'yyyy-MM-dd')
      const endDate = format(endOfMonth(now), 'yyyy-MM-dd')

      // Call reload function with current month dates and maintain current filters
      reloadFunction(
        1,
        currentFilters.properties,
        currentFilters.partners,
        currentFilters.upcomingSchedules,
        startDate,
        endDate,
        currentFilters.byCreatedAt,
        currentFilters.byCheckIn,
        currentFilters.showNormal,
        currentFilters.showSpecial
      )
    }
    // If switching to agenda mode, call reloadFunction with 3 months range and current filters
    else if (newMode === ViewMode.AGENDA) {
      const { startDate, endDate } = getThreeMonthsRange(currentCalendarDate)
      reloadFunction(
        1,
        currentFilters.properties,
        currentFilters.partners,
        currentFilters.upcomingSchedules,
        startDate,
        endDate,
        currentFilters.byCreatedAt,
        currentFilters.byCheckIn,
        currentFilters.showNormal,
        currentFilters.showSpecial
      )
    }
  }

  const renderEventContent = (eventInfo: any) => {
    // Só mostra o título no primeiro dia do evento (quando isStart é true)
    const shouldShowTitle = eventInfo.isStart || !eventInfo.event.allDay
    
    return (
      <div style={{ 
        padding: '5px', 
        paddingLeft: '10px',
        height: '25px',
        display: 'flex',
        alignItems: 'center',
        minHeight: '25px'
      }}>
        {shouldShowTitle && <b>{String(eventInfo.event.title).toUpperCase()}</b>}
      </div>
    )
  }

  // Componente para mostrar o período de datas
  const renderDateRangeInfo = () => {
    // Pegar as datas dos filtros atuais ou usar o mês atual como fallback
    let startDate = ''
    let endDate = ''
    
    // Se estivermos no modo lista, usar as datas do mês atual
    if (viewMode === ViewMode.LIST) {
      const now = new Date()
      startDate = format(startOfMonth(now), 'yyyy-MM-dd')
      endDate = format(endOfMonth(now), 'yyyy-MM-dd')
    }
    
    // Verificar se temos dados para determinar o período real
    if (schedule.length > 0) {
      const dates = schedule.map(item => parseISO(String(item.checkIn || item.date_initial)))
      const minDate = new Date(Math.min(...dates.map(d => d.getTime())))
      const maxDate = new Date(Math.max(...dates.map(d => d.getTime())))
      
      startDate = format(minDate, 'yyyy-MM-dd')
      endDate = format(maxDate, 'yyyy-MM-dd')
    }

    const formattedStartDate = format(parseISO(startDate), 'dd \'de\' MMMM \'de\' yyyy', { locale: ptBR })
    const formattedEndDate = format(parseISO(endDate), 'dd \'de\' MMMM \'de\' yyyy', { locale: ptBR })

    return (
      <Box 
        bg="gray.50" 
        p={4} 
        borderRadius="md" 
        border="1px solid" 
        borderColor="gray.200"
        mb={4}
      >
        <Text fontSize="md" fontWeight="500" color="gray.700">
          Mostrando agendamentos entre os dias{' '}
          <Text as="span" fontWeight="bold" color="green.dark">
            {formattedStartDate}
          </Text>
          {' '}à{' '}
          <Text as="span" fontWeight="bold" color="green.dark">
            {formattedEndDate}
          </Text>
        </Text>
        <Text fontSize="sm" color="gray.600" mt={1}>
          Total de {schedule.length} agendamento{schedule.length !== 1 ? 's' : ''} encontrado{schedule.length !== 1 ? 's' : ''}
        </Text>
      </Box>
    )
  }

  const renderListView = () => {
    if (schedule.length === 0) {
      return (
        <VStack spacing={4} align="stretch">
          {renderDateRangeInfo()}
          <Text fontSize='19px' fontWeight='500'>Não foram encontrados agendamentos</Text>
        </VStack>
      )
    }

    return (
      <VStack spacing={4} align="stretch">
        {/* Informação do período de datas */}
        {renderDateRangeInfo()}
        
        {/* Tabela */}
        <Table colorScheme="blue">
          <Thead>
            <Tr>
              <Th backgroundColor="green.dark" color="white">Propriedade</Th>
              <Th backgroundColor="green.dark" color="white">Usuário</Th>
              <Th backgroundColor="green.dark" color="white">Data Especial</Th>
              <Th backgroundColor="green.dark" color="white">Data Inicial</Th>
              <Th backgroundColor="green.dark" color="white">Data Final</Th>
              <Th backgroundColor="green.dark" color="white">Data de Criação</Th>
              <Th backgroundColor="green.dark" color="white">Status</Th>
              <Th backgroundColor="green.dark" color="white">Ações</Th>
            </Tr>
          </Thead>
          <Tbody>
            {paginatedData.map((item) => (
              <Tr key={item.id} _hover={{ bg: "gray.50" }}>
                <Td>
                  <Flex align="center">
                    <Box
                      w={4}
                      h={4}
                      borderRadius="full"
                      bg={item.property.color}
                      mr={3}
                    />
                    <Text fontWeight="500">{item.property.name}</Text>
                  </Flex>
                </Td>
                <Td>
                  <Text>{item.user.name}</Text>
                </Td>
                <Td>
                  <Badge 
                    colorScheme={item.isSpecialReservation ? 'green' : 'white'}
                    variant="subtle"
                  >
                    {item.isSpecialReservation ? 'Reserva Especial' : '--'}
                  </Badge>
                </Td>
             
                <Td>
                  <Text>{format(parseISO(String(item.checkIn)), 'dd/MM/yyyy')}</Text>
                </Td>
                <Td>
                <Text>{format(parseISO(String(item.checkOut)), 'dd/MM/yyyy')}</Text>
                </Td>
                <Td>
                  <Text>{format(parseISO(String(item.created_at)), 'dd/MM/yyyy HH:mm')}</Text>
                </Td>
                <Td>
                  <Badge 
                    colorScheme={item.status === 1 ? 'green' : 'yellow'}
                    variant="subtle"
                  >
                    {item.status === 1 ? 'Confirmado' : 'Pendente'}
                  </Badge>
                </Td>
                <Td>
                  <Button
                    size="sm"
                    colorScheme="green"
                    variant="outline"
                    onClick={() => handleViewItemDetails(item.id)}
                  >
                    Detalhes
                  </Button>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>

        {/* Paginação */}
        <Pagination
          totalCountOfRegisters={schedule.length}
          registersPerPage={itemsPerPage}
          currentPage={currentPage}
          onPageChange={handlePageChange}
        />
      </VStack>
    )
  }

  return (
    <Box>
      {selectedItem.show && <ModalAppointmentDetails appointment_id={selectedItem?.id} onClose={() => setSelectedItem({ show: false, id: '' })} reloadFunction={reloadFunction} />}

      {/* Botões de alternância de modo */}
      <Flex mb={6} justify="space-between" align="center">
        <Heading size="md">Agendamentos</Heading>
        <ButtonGroup size="sm" isAttached variant="outline">
          <Button
            colorScheme={viewMode === ViewMode.AGENDA ? "green" : "gray"}
            variant={viewMode === ViewMode.AGENDA ? "solid" : "outline"}
            onClick={() => handleViewModeChange(ViewMode.AGENDA)}
          >
            Modo Agenda
          </Button>
          <Button
            colorScheme={viewMode === ViewMode.LIST ? "green" : "gray"}
            variant={viewMode === ViewMode.LIST ? "solid" : "outline"}
            onClick={() => handleViewModeChange(ViewMode.LIST)}
          >
            Modo Lista
          </Button>
        </ButtonGroup>
      </Flex>

      {/* Renderização condicional baseada no modo */}
      {viewMode === ViewMode.AGENDA ? (
        ((schedule?.length) !== 0) ? (
          <FullCalendar
            ref={calendarRef}
            plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
            initialView="dayGridMonth"
            headerToolbar={{
              left: 'prev,next today',
              center: 'title',
              right: 'dayGridMonth,timeGridWeek,timeGridDay'
            }}
            weekends
            selectable
            displayEventTime={false}
            events={appointmentsData}
            eventContent={renderEventContent}
            // dateClick={handleSelect}
            eventClick={handleUpdateEvent}
            datesSet={handleCalendarNavigation} // Callback para navegação do calendário
            locale={ptBrLocale}
            height="auto"
            dayMaxEvents={true} // Permite scroll quando há muitos eventos
            moreLinkClick="popover" // Mostra popover com eventos extras
            eventDisplay="block" // Exibe eventos como blocos compactos
            slotEventOverlap={false} // Impede sobreposição de eventos
            eventOverlap={false} // Impede sobreposição geral de eventos
            allDaySlot={true} // Garante que a área all-day seja exibida
          />
        ) : <Text fontSize='19px' fontWeight='500'>Não foram encontrados agendamentos</Text>
      ) : (
        renderListView()
      )}
    </Box>

  )
}

export { CalendarSchedule }
